import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "react-toastify";
import { OverlaySpinner, Spinner } from "../atoms/spinner";
import { AuthLayout } from "../layouts/Auth";
import {
  createSession,
  getRedirectUrlDefault,
  getRedirectUrlLinkIdentity,
  getRedirectUrlLogIn,
} from "../api/v2/requests";
import {
  type CurrentUser,
  FetchError,
  sessionStatusExpired,
  sessionStatusParam,
} from "../api/types";
import { useTranslation } from "react-i18next";
import { Button } from "~/atoms/button";
import cn from "classnames";
import { useAuthContext } from "../hooks/useAuthContext";
import { getFirstStep, AuthStep } from "../config/authFlow";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "~/atoms/input";
import { type SsoConfig, useSsoConfig } from "../api/v2/useSsoConfig";

const redirectParam = "redirect";
const emailParam = "email";

export const LoginContainer = () => {
  const { t } = useTranslation();
  const { data: ssoConfig, isLoading } = useSsoConfig();

  if (isLoading) {
    return <OverlaySpinner />;
  }

  if (!ssoConfig) {
    return (
      <div className="grid h-screen place-items-center">
        {t(($) => $.toasts.error.generic)}
      </div>
    );
  }

  return <Login ssoConfig={ssoConfig} />;
};

const Login = ({ ssoConfig }: { ssoConfig: SsoConfig }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionStatus = searchParams.get(sessionStatusParam);
  const redirect = searchParams.get(redirectParam);
  const emailParamValue = searchParams.get(emailParam);
  const { setUser } = useAuthContext();

  const [email, setEmail] = useState(emailParamValue ?? "");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<AuthStep>(getFirstStep(ssoConfig));
  const [userTempStorage, setUserTempStorage] = useState<CurrentUser>();
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  const pwdRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);

  // Handle external redirects in effect
  useEffect(() => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [redirectUrl]);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const timeout = setTimeout(() => setIsSubmitting(true), 300);

    try {
      if (
        step === AuthStep.EMAIL ||
        step === AuthStep.SSO ||
        step === AuthStep.ADD_SSO
      ) {
        const res = await getRedirectUrl(step);
        setRedirectUrl(res.redirectUrl);
        return;
      }

      const res = await createSession(email, password, token);

      if (res.ssoAvailable && !res.ssoActivated) {
        setStep(AuthStep.ADD_SSO);
        setIsSubmitting(false);
        setUserTempStorage(res.user);
        clearTimeout(timeout);
      } else {
        setUser(res.user);
        await navigate(redirect ?? "/");
      }
    } catch (error) {
      clearTimeout(timeout);
      setIsSubmitting(false);

      if (error instanceof FetchError) {
        handleFetchError(error);
      } else {
        toast.error(t(($) => $.toasts.error.generic));
      }
    }
  };

  const getRedirectUrl = async (
    step: AuthStep.EMAIL | AuthStep.SSO | AuthStep.ADD_SSO,
  ) => {
    switch (step) {
      case AuthStep.EMAIL:
        return getRedirectUrlLogIn(email, redirect ?? undefined);
      case AuthStep.SSO:
        return getRedirectUrlDefault(redirect ?? undefined);
      case AuthStep.ADD_SSO:
        return getRedirectUrlLinkIdentity();
    }
  };

  const handleFetchError = (error: FetchError) => {
    if (step === AuthStep.EMAIL && error.code === "NOT_FOUND") {
      setStep(AuthStep.PASSWORD);
    } else if (error.code === "TWO_FA_REQUIRED") {
      setStep(AuthStep.TWO_FA);
    } else if (error.code === "INVALID_CREDENTIALS") {
      toast.error(t(($) => $.toasts.error.incorectPass));
    } else {
      toast.error(t(($) => $.toasts.error.generic));
    }
  };

  const handleShowPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };

  const handleSkipButton = () => {
    setUser(userTempStorage);
    if (redirect && /^https?:\/\//i.test(redirect)) {
      // Only redirect to same origin for security
      try {
        const redirectUrlParsed = new URL(redirect);
        const currentUrl = new URL(window.location.href);
        if (redirectUrlParsed.origin === currentUrl.origin) {
          setRedirectUrl(redirect);
          return;
        }
      } catch {
        // Invalid URL, fall through to navigate
      }
    }
    void navigate(redirect ?? "/");
  };

  useEffect(() => {
    if (step === AuthStep.PASSWORD) {
      pwdRef.current?.focus();
    } else if (step === AuthStep.TWO_FA) {
      otpRef.current?.focus();
    }
  }, [step]);

  useEffect(() => {
    if (sessionStatus === sessionStatusExpired) {
      setUser(undefined);
      toast.info(t(($) => $.toasts.info.sessionExpired));
    }
  }, [sessionStatus, setUser, t]);

  switch (step) {
    case AuthStep.EMAIL:
    case AuthStep.PASSWORD:
    case AuthStep.EMAIL_AND_PASSWORD:
      return (
        <AuthLayout
          title={t(($) => $.commons.auth.loginTitle)}
          description={t(($) => $.commons.auth.loginDescription)}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="pb-2 text-sm">
                {t(($) => $.commons.auth.email)}
              </label>
              <Input
                name="email"
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                required
                autoFocus
              />
            </div>
            <div
              className={cn("relative", {
                hidden:
                  ssoConfig.ssoEnabled &&
                  !ssoConfig.ssoOnly &&
                  step === AuthStep.EMAIL,
              })}
            >
              <label className="pb-2 text-sm">
                {t(($) => $.commons.auth.password)}
              </label>
              <Input
                ref={pwdRef}
                name="password"
                type={showPassword ? "text" : "password"}
                onChange={(e) => setPassword(e.target.value)}
                value={password}
                required={step === AuthStep.PASSWORD}
              />
              <Button
                type="button"
                variant="ghost"
                onClick={handleShowPassword}
                size="sm"
                className="absolute top-7 right-4 z-10 cursor-pointer px-1 hover:bg-transparent"
              >
                {showPassword ? <Eye /> : <EyeOff />}
              </Button>
            </div>
            <div className="flex w-full items-center justify-between">
              <Button type="submit" disabled={isSubmitting} className="w-1/4">
                {isSubmitting ? (
                  <Spinner className="h-5 w-5" />
                ) : (
                  t(($) => $.commons.buttons.proceed)
                )}
              </Button>
              <Link to="/forgot-password" className="text-sm">
                {t(($) => $.commons.auth.forgotPassword)}
              </Link>
            </div>
          </form>
        </AuthLayout>
      );
    case AuthStep.SSO:
      return (
        <AuthLayout
          title={t(($) => $.commons.auth.loginTitle)}
          description={t(($) => $.commons.auth.ssoDescription)}
        >
          <form onSubmit={handleSubmit} className="flex flex-col">
            <Button type="submit">{t(($) => $.commons.auth.ssoLogin)}</Button>
          </form>
          <div className="flex justify-center pt-4">
            <Button
              onClick={() => setStep(AuthStep.EMAIL_AND_PASSWORD)}
              className="text-xs"
              variant="secondary"
            >
              {t(($) => $.commons.auth.emailPasswordLogin)}
            </Button>
          </div>
        </AuthLayout>
      );
    case AuthStep.TWO_FA:
      return (
        <AuthLayout
          title={t(($) => $.commons.auth.twoFaTitle)}
          description={t(($) => $.commons.auth.twoFaDescription)}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="pb-1 text-sm">
                {t(($) => $.commons.auth.twoFaToken)}
              </label>
              <Input
                ref={otpRef}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                required
              />
            </div>
            <div className="flex items-center justify-between">
              <Button
                className="w-1/4"
                disabled={isSubmitting || !token}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <Spinner className="h-5 w-5" />
                ) : (
                  t(($) => $.commons.buttons.proceed)
                )}
              </Button>
              <Link to="/restore-account" className="text-sm">
                {t(($) => $.commons.auth.restoreAccount)}
              </Link>
            </div>
          </form>
        </AuthLayout>
      );
    case AuthStep.ADD_SSO:
      return (
        <AuthLayout
          title="commons.auth.addSsoTitle"
          description="commons.auth.addSsoDescription"
        >
          <div className="flex gap-2">
            <Button
              className="w-1/4"
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? (
                <Spinner className="h-5 w-5" />
              ) : (
                t(($) => $.commons.auth.addSsoButton)
              )}
            </Button>
            <Button
              variant="secondary"
              disabled={isSubmitting}
              onClick={handleSkipButton}
            >
              {t(($) => $.commons.auth.skipButton)}
            </Button>
          </div>
        </AuthLayout>
      );
    default:
      return (
        <div>
          <h1>{t(($) => $.toasts.error.generic)}</h1>
        </div>
      );
  }
};
